import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import Grid from "../../atoms/Grid/Grid";
import {
  CREDIT_APPROVAL_STATUS,
  CREDIT_HOLD_STATUS,
  CREDIT_REASON,
  CREDIT_TYPE,
} from "../../../helpers/const";
import { endpoints } from "../../../api/endpoints";
import {
  getCreditsByFilter,
  getCustomerLoginAs,
} from "../../../store/slices/CustomerSlice/CustomerSlice";

const creditHeaderAttributes = [
  {
    id: "credit_memo_num",
    label: "Memo #",
    tooltip: "Credit Memo Number",
    sortable: true,
    searchable: {
      label: "Search by Credit Memo Number",
      type: "text",
      width: 4,
    },
    width: "8%",
  },

  {
    id: "credit_reason",
    label: "Credit Reason",
    sortable: true,
    width: "12%",
  },
  {
    id: "ref_invoice",
    label: "Ref Invoice",
    sortable: false,
    searchable: {
      label: "Search by Reference Invoice Number",
      type: "text",
      width: 4,
    },
    width: "10%",
  },
  {
    id: "po",
    label: "PO",
    sortable: false,
    width: "8%",
  },
  {
    id: "date_entered",
    label: "Date",
    tooltip: "Date Entered",
    sortable: true,
    width: "10%",
  },
  {
    id: "total",
    label: "Total",
    sortable: true,
    width: "10%",
  },
  {
    id: "agent",
    label: "Agent",
    sortable: true,
    width: "10%",
  },
  {
    id: "return_address",
    label: "Return Information",
    sortable: false,
    width: "15%",
  },
  {
    id: "status",
    label: "Status",
    sortable: true,
    width: "12%",
  },
  {
    id: "details",
    label: "Details",
    sortable: false,
    width: "8%",
  },
];

const returnRealAttributeName = (attr) => {
  switch (attr) {
    case "credit_memo_num":
      return "credit_memo_num";
    case "ref_invoice":
      return "reference_invoice_num";
    case "date_entered":
      return "date_entered";
    case "return_address":
      return "return_address";
    default:
      return attr;
  }
};

const fetchCreditData = async (dispatch, params) => {
  const creditParams = {
    credit_memo_num: params.credit_memo_num,
    ref_invoice: params.ref_invoice,
    return_address: params.return_address,
    page: params.page,
    customerNum: params.customerNumbers, // This will be comma-delimited string
    order_by: returnRealAttributeName(params.sortBy),
    sort_order: params.sortOrder === "desc" ? "dsc" : "asc",
    status: params.select,
    from: params.from,
    to: params.to,
  };

  const result = await dispatch(getCreditsByFilter(creditParams));

  if (!result?.error) {
    return {
      count: result.payload.count,
      results: result.payload.results,
    };
  }

  return { count: 0, results: [] };
};

const mapCreditData = (credits) => {
  return credits.map((d) => ({
    id: d.credit_memo_num,
    credit_memo_num: (
      <Link
        to={`/credit-detail/${d.credit_memo_num}`}
        className="text-[var(--primary-color)] hover:text-[var(--primary-color-dark)] underline"
      >
        {d.credit_memo_num}
      </Link>
    ),
    credit_type:
      CREDIT_TYPE.find((type) => type.id === parseInt(d.credit_type))?.label ||
      "Unknown",
    credit_reason:
      CREDIT_REASON.find((reason) => reason.id === parseInt(d.credit_reason))
        ?.label || "Unknown",
    ref_invoice: d.reference_invoice_num || "-",
    po: d.apply_to_po || "-",
    date_entered: d.date_entered
      ? dayjs(d.date_entered).format("MM/DD/YYYY")
      : "-",
    total: d.memo_amount ? `$${parseFloat(d.memo_amount).toFixed(2)}` : "$0.00",
    agent: d.entered_by || "-",
    return_address: `${d.return_customer_name || ""}
${d.return_street || ""}
${d.return_route || ""}
${d.return_city ? d.return_city + ", " : ""}${d.return_state || ""} ${d.return_zip || ""}`.trim(),
    status: (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          d.status === "0"
            ? "bg-yellow-100 text-yellow-800"
            : d.status === "1"
              ? "bg-green-100 text-green-800"
              : "bg-gray-100 text-gray-800"
        }`}
      >
        {d.status === "0"
          ? `${CREDIT_APPROVAL_STATUS.find((status) => status.id === parseInt(d.status))?.label || "Unknown"} - ${CREDIT_HOLD_STATUS.find((status) => status.id === parseInt(d.hold_status))?.label || "Unknown"}`
          : CREDIT_APPROVAL_STATUS.find(
              (status) => status.id === parseInt(d.status)
            )?.label || "Unknown"}
      </span>
    ),
    details: (
      <Link
        to={`/credit-detail/${d.credit_memo_num}`}
        target="_blank"
        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-[var(--primary-color)] hover:bg-[var(--primary-color-dark)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[var(--primary-color)]"
      >
        View
      </Link>
    ),
  }));
};

export default function CreditHistory() {
  const dispatch = useDispatch();
  const { loginOptions } = useSelector((state) => state.customer);

  // Fetch customer list on component mount
  useEffect(() => {
    dispatch(getCustomerLoginAs());
  }, [dispatch]);

  // Transform loginOptions to dropdown options
  const customerOptions =
    loginOptions?.map((customer) => ({
      id: customer.id.toString(),
      value: customer.id.toString(),
      label: `${customer.id} - ${customer.label}`,
      customerName: customer.label,
    })) || [];

  const customFilters = [
    {
      id: "customerNumbers",
      label: "Filter by Customer Numbers",
      placeholder: "Select customer numbers...",
      type: "autocomplete-multiselect",
      options: customerOptions,
    },
  ];

  return (
    <div className="p-6">
      <div className="mt-6">
        <Grid
          title="Credit History"
          headerAttributes={creditHeaderAttributes}
          fetchDataFunction={fetchCreditData}
          mapDataFunction={mapCreditData}
          loadingSelector={(state) => state.loaderSpinner?.loading}
          rowsPerPage={50}
          defaultSortBy="date_entered"
          defaultSortOrder="desc"
          showFilters={true}
          showDateFilter={true}
          showPagination={true}
          customFilters={customFilters}
        />
      </div>
    </div>
  );
}
